import { NgModule } from '@angular/core';
import { CommonModule, DatePipe, CurrencyPipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SharedModule } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { TooltipModule } from 'primeng/tooltip';
import { OverlayModule } from 'primeng/overlay';
import { ScrollerModule } from 'primeng/scroller';
import { DropdownModule } from 'primeng/dropdown';
import { TableModule } from 'primeng/table';
import { TabViewModule } from 'primeng/tabview';
import { TagModule } from 'primeng/tag';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { SearchIcon } from 'primeng/icons/search';
import { CheckIcon } from 'primeng/icons/check';
import { TimesIcon } from 'primeng/icons/times';
import { TimesCircleIcon } from 'primeng/icons/timescircle';
import { CheckboxModule } from 'primeng/checkbox';
import { ChipsModule } from 'primeng/chips';
import { LightgalleryModule } from 'lightgallery/angular';
import { AceModule } from 'ngx-ace-wrapper';
import { TranslateLabelsPipe } from './pipes/translate-labels.pipe';
import { TenantsService } from './services/tenants/tenants.service';
import { ApiKeysService } from './services/apiKeys/apiKeys.service';
import { ConnectedDeviceSettingsService } from './services/connectedDeviceSettings/connected-device-settings.service';
import { GroupsService } from './services/groups/groups.service';
import { IssueTypeService } from './services/issue/issue-type.service';
import { CustomAttributesService } from './services/customAttributes/customAttributesService';
import { FormVariantsService } from './services/form-variants/form-variants.service';
import { TemplateVariantsService } from './services/template-variants/template-variants.service';
import { IssueStatusService } from './services/issue/issue-status.service';

import { ACTIVITY_COMPONENTS, ACTIVITY_COMPONENT_LIST, createActivityComponentRegistry } from './components/activity/components/activityComponent';
import { ActivityRendererComponent } from './components/activity/activity-component-renderer/activity-component-renderer.component';
import { ActivityStreamComponent } from './components/activity-stream/activity-stream.component';
import { IssueListComponent } from './components/issue-list/issue-list.component';
import { IssueViewComponent } from './components/issue-view/issue-view.component';
import { IssueViewV2SidebarWrapperComponent } from './components/issue-view/issue-view-sidebar-wrapper.component';
import { IssuesSettingsPageComponent } from './components/issues-settings-page/issues-settings-page.component';
import { LabelListComponent } from './components/label-list/label-list.component';
import { LabelViewComponent } from './components/label-view/label-view.component';
import { TaskSettingsPageComponent } from './components/task-settings-page/task-settings-page.component';
import { FormsListComponent } from './components/forms-list/forms-list.component';
import { FormVariantComponent } from './components/form-variant/form-variant.component';
import { TemplatesComponent } from './components/templates/templates.component';
import { TemplateVariantComponent } from './components/template-variant/template-variant.component';
import { EditSelectV2InputComponent } from './components/edit-select-v2-form-control/edit-select-v2-form-control.component';
import { EditDateTimePeriodInputV2Component } from './components/edit-dueDateTime-form-control-v2/edit-dueDateTime-form-control-v2.component';
import { NxTemplateDirective } from './directives/nx-template.directive';
import { DefaultButtonComponent } from './components/default-button/default-button.component';
import { DefaultBoxComponent } from './components/default-box/default-box.component';
import { DefaultBoxV2Component } from './components/default-box-v2/default-box-v2.component';
import { HeadlineBoxComponent } from './components/headline-box/headline-box.component';

// Admin page components (Phase 2)

import { BillingComponent } from './components/billing/billing.component';
import { CompanyViewComponent } from './components/company-view/company-view.component';
import { DeveloperPageComponent } from './components/developer-page/developer-page.component';
import { GroupListComponent } from './components/group-list/group-list.component';
import { GroupViewComponent } from './components/group-view/group-view.component';
import { MobileDeviceListComponent } from './components/mobile-device-list/mobile-device-list.component';
import { MobileDeviceViewComponent } from './components/mobile-device-view/mobile-device-view.component';
import { NoAccessPageComponent } from './components/no-access-page/no-access-page.component';
import { TenantListComponent } from './components/tenant-list/tenant-list.component';
import { TenantViewComponent } from './components/tenant-view/tenant-view.component';
import { TimeEntryCategoryListComponent } from './components/time-entry-category-list/time-entry-category-list.component';
import { TimeEntryCategoryViewComponent } from './components/time-entry-category-view/time-entry-category-view.component';
import { UserProfileComponent } from './components/user-profile/user-profile.component';
import { UserViewComponent } from './components/user-view/user-view.component';
import { UserListComponent } from './components/users-list/user-list.component';

// Helper dialogs (Phase 2)
import { CompanyDialogComponent } from './components/company-dialog/company-dialog.component';
import { GroupDialogComponent } from './components/group-dialog/group-dialog.component';
import { AddRolesDialogComponent } from './components/add-roles-dialog/add-roles-dialog.component';
import { AppVersionsDialogComponent } from './components/app-versions-dialog/app-versions-dialog.component';
import { TenantDialogComponent } from './components/tenant-dialog/tenant-dialog.component';
import { TimeEntryCategoryDialogDialogComponent } from './components/time-entry-category-dialog/time-entry-category-dialog.component';
import { UserPreferredLanguageDialogComponent } from './components/user-preferred-language-dialog/user-preferred-language-dialog.component';
import { UserDialogComponent } from './components/user-dialog/user-dialog.component';
import { UserLicenceDialogComponent } from './components/user-licence-dialog/user-licence-dialog.component';
import { UserPasswordDialogComponent } from './components/user-password-dialog/user-password-dialog.component';
import { JoinPipe } from './pipes/join.pipe';
import { ArrayTranslatePipe } from './pipes/array-translate.pipe';

// Generic dialog components (migrated 1:1 from @nexato/nx-core-module, #666)
import { NxConfirmDialogComponent } from './components/confirmDialog/confirmDialogComponent';
import { EditDialogComponent, EditDialogDynamicContentDirective } from './components/editDialog/editDialogComponent';
import { ViewDialogComponent, ViewDialogDynamicContentDirective } from './components/viewDialog/viewDialogComponent';

// Domain dialog components (migrated 1:1 from @nexato/nx-core-module, #669)
import { IssueCreateDialogComponent } from './components/issue-create-dialog/issue-create-dialog.component';
import { TaskCreateDialogComponent } from './components/task-create-dialog/task-create-dialog.component';
import { LabelDialogComponent } from './components/label-dialog/label-dialog.component';

// Icon & mini-view display components (migrated 1:1 from @nexato/nx-core-module, #672)
import { RelativeTimeComponent } from './components/relative-time/relative-time.component';
import { UserNameComponent } from './components/user-name/user-name.component';
import { LabelComponent } from './components/label/label.component';
import { CommentsIconComponent } from './components/comments-icon/comments-icon.component';
import { FilesIconComponent } from './components/files-icon/files-icon.component';
import { NoteIconComponent } from './components/note-icon/note-icon.component';
// formatRelativeTime pipe — transitive dependency of RelativeTimeComponent (#672)
import { RelativeTimePipe } from './pipes/relative-time.pipe';
// formatFutureRelativeTime pipe — Job-Verwaltung, naechste Ausfuehrung eines Triggers (#746)
import { FutureRelativeTimePipe } from './pipes/future-relative-time.pipe';
// Job-Verwaltung: Admin-Uebersicht fuer Quartz-Jobs und ihre Trigger (#746)
import { TriggerStateBadgeComponent } from './components/job-admin/trigger-state-badge.component';
import { TriggerTableComponent } from './components/job-admin/trigger-table.component';
import { JobListComponent } from './components/job-list/job-list.component';
import { JobDetailComponent } from './components/job-detail/job-detail.component';

// Box & Button UI primitives (migrated 1:1 from @nexato/nx-core-module, #673)
import { EmptyBoxComponent } from './components/empty-box/empty-box.component';
import { PageTitleBoxComponent } from './components/pageTitle-box/pageTitle-box.component';
import { CommandButtonComponent } from './components/command-button/command-button.component';
import { NxButtonComponent } from './components/nx-button/nx-button.component';

// Edit form controls — Text/Select (migrated 1:1 from @nexato/nx-core-module, #674)
import { EditTextInputComponent } from './components/edit-text-form-control/edit-text-form-control.component';
import { EditTextfieldInputComponent } from './components/edit-textfield-form-control/edit-textfield-form-control.component';
import { EditSelectInputComponent } from './components/edit-select-form-control/edit-select-form-control.component';
import { EditSelectCountryInputComponent } from './components/edit-selectCountry-form-control/edit-selectCountry-form-control.component';
import { EditMultiSelectInputComponent } from './components/edit-multiSelect-form-control/edit-multiSelect-form-control.component';
// nxAutoResize — required template dependency of EditTextfieldInputComponent (#674)
import { AutoResizeTextareaDirective } from './directives/auto-resize-textarea.directive';
// Edit form control — Color (never migrated from @nexato/nx-core-module during #669, #712)
import { EditColorInputComponent } from './components/edit-color-form-control/edit-color-form-control.component';
// Edit form control — Chips (never migrated from @nexato/nx-core-module during #669, referenced by location-dialog)
import { EditChipsInputComponent } from './components/edit-chips-form-control/edit-chips-form-control.component';
// Announcement-Verwaltung fuer Nexato (#738)
import { AnnouncementBodyInputComponent } from './components/announcement-body-input/announcement-body-input.component';
import { AnnouncementFormComponent } from './components/announcement-form/announcement-form.component';
import { AnnouncementListComponent } from './components/announcement-list/announcement-list.component';
import { AnnouncementRowComponent } from './components/announcement-row/announcement-row.component';
// Notification-Einstellungen im Nutzerprofil (#735)
import { NotificationAdminDetailComponent } from './components/notification-admin-detail/notification-admin-detail.component';
import { NotificationAdminListComponent } from './components/notification-admin-list/notification-admin-list.component';
import { NotificationAdminRowComponent } from './components/notification-admin-row/notification-admin-row.component';
import { NotificationDeliveryListComponent } from './components/notification-delivery-list/notification-delivery-list.component';
import { NotificationDeliveryRowComponent } from './components/notification-delivery-row/notification-delivery-row.component';
import { NotificationDiagnosticsPageComponent } from './components/notification-diagnostics/notification-diagnostics-page.component';
import { NotificationSettingsListComponent } from './components/notification-settings-list/notification-settings-list.component';
import { NotificationTypeRowComponent } from './components/notification-type-row/notification-type-row.component';

// Filter controls (migrated 1:1 from @nexato/nx-core-module, #676)
import { NxFilterMultiSelectComponent, NxFilterMultiSelectItemComponent } from './components/filter-multiselect/nx-filter-multiselect';
import { NxFilterNumberSelectComponent } from './components/filter-numberselect/nx-filter-numberselect';

// Time-/date-entry controls + their dependencies (migrated 1:1 from @nexato/nx-core-module, #675)
import { AnchorDateTimeInputComponent } from './components/anchor-datetime-input/anchor-datetime-input.component';
import { EditTimeEntriesInputComponent } from './components/edit-time-entries-input/edit-time-entries-input.component';
import { TimeEntryDialogDialogComponent } from './components/time-entry-dialog/time-entry-dialog.component';
import { SecondsToDurationPipe } from './pipes/secondsToDuration.pipe';
import { TimeEntryService } from './services/timeEntry/timeEntryService';

// File- & TimeEntry-views (migrated 1:1 from @nexato/nx-core-module, #677)
import { FileListComponent } from './components/file-list/file-list.component';
import { FileGalleryDisplayComponent } from './components/file-gallery-display/file-gallery-display.component';
import { TimeEntryListComponent } from './components/timeEntry-list/timeEntry-list.component';
import { TimeEntriesSumComponent } from './components/time-entries-sum-view/time-entries-sum-view.component';
import { TimeEntryCategoriesSumComponent } from './components/time-entry-categories-sum-view/time-entry-categories-sum-view.component';
// File service backing FileListComponent (migrated 1:1 from @nexato/nx-core-module, #677)
import { FilesService } from './services/file/file.service';

// Generic UI widgets (migrated 1:1 from @nexato/nx-core-module, #680)
import { CountBadgeComponent } from './components/count-badge/count-badge.component';
import { NxFilterDropdownComponent, NxFilterDropdownItemComponent } from './components/filter-dropdown/nx-filter-dropdown';
import { PageTitleBoxToplineComponent } from './components/pageTitle-box-topline/pageTitle-box-topline.component';
import { SimplePaginatorComponent } from './components/simple-paginator/simple-paginator.component';
import { TableHeaderComponent } from './components/table-header/table-header.component';

// Entity-link display components (migrated 1:1 from @nexato/nx-core-module, #681)
import { EntityLinkListComponent } from './components/entity-link-list/entity-link-list.component';
import { SingleEntityLinkWidgetComponent } from './components/single-entity-link-widget/single-entity-link-widget.component';
// LinkableService backing the single-entity-link-widget (transitive sub-dependency, #681)
import { LinkableService } from './services/entity-link/linkable/linkable.service';
// <p-progressSpinner> used by single-entity-link-widget (#681)
import { ProgressSpinnerModule } from 'primeng/progressspinner';

// Issue display/type components (migrated 1:1 from @nexato/nx-core-module, #682)
import { IssueRowComponent } from './components/issue/issue-row/issue-row.component';
import { IssueTypeIconComponent } from './components/issue/issue-type-icon.component';
import { IssueTypesComponent } from './components/issue/issueTypes/issue-types.component';
import { IssueStatusGrojupsComponent } from './components/issue/issueStatusGroups/issue-types.component';
// IssueRowRenderer backing the entity-row registry for Issue (transitive sub-dependency, #682)
import { IssueRowRenderer } from './components/issue/issue-row/issue-row-renderer';
// IssueStatusGroupService backing app-issue-status-groups (transitive sub-dependency, #682)
import { IssueStatusGroupService } from './services/issue/issueStatusGroups/issueStatusGroup.service';

// Issue command widgets + label/activity widget (migrated 1:1 from @nexato/nx-core-module, #683)
import { AssignIssueWidgetComponent } from './components/issue/assign/assign-issue-widget.component';
import { ChangeIssueStatusWidgetComponent } from './components/issue/changeIssueStatus/change-issue-status-widget.component';
import { IssueDueDateTimeWidgetComponent } from './components/issue/dueDateTime/dueDateTime-widget.component';
import { SelectFormWidgetComponent } from './components/issue/selectForm/select-form-widget.component';
import { UpdateIssueLocationWidgetComponent } from './components/issue/updateLocation/update-issue-location-widget.component';
import { IssueLocationWidgetInputComponent } from './components/issue/updateLocation/issue-location-widget-input.component';
import { LabelWidgetComponent } from './components/label/label-widget/label-widget.component';
import { LabelWidgetInputComponent } from './components/label/label-widget/label-widget-input.component';
import { ActivityCommentCreatedCreateCommandComponent } from './components/activity/activity-comment-created-create-command/activity-comment-created-create-command.component';
// nx-edit-multiselect-v2-input — required @ViewChild type of label-widget (transitive sub-dependency, #683)
import { EditMultiSelectV2InputComponent } from './components/edit-multiSelect-v2-form-control/edit-multiSelect-v2-form-control.component';
// LabelService backing label-widget-input (transitive sub-dependency, #683)
import { LabelService } from './services/label/label.service';
// Injectable services injected by the migrated widgets (not providedIn:'root'):
// WidgetCoordinatorService, LocationService, RolesService (#683)
import { WidgetCoordinatorService } from './services/issue/widget-coordinator.service';
import { LocationService } from './services/location/location.service';
import { RolesService } from './services/roles/roles.service';
// p-multiSelect used by edit-multiSelect-v2-form-control (#683)
import { MultiSelectModule } from 'primeng/multiselect';

// Admin-/Developer-page content components (migrated 1:1 from @nexato/nx-core-module, #684)
import { ApiKeysComponent } from './components/api-keys/api-keys.component';
import { CustomAttributesComponent } from './components/custom-attributes/custom-attributes.component';
import { CustomAttributeSectionComponent } from './components/custom-attributes/custom-attribute-section.component';
import { CustomAttributeDefinitionComponent } from './components/custom-attributes/custom-attribute-definition.component';
import { EventsComponent } from './components/events/events.component';
import { HttpLogsComponent } from './components/http-logs/http-logs.component';
import { SubscriptionsComponent } from './components/subscriptions/subscriptions.component';
import { TaskTypesComponent } from './components/task/taskTypes/task-types.component';
// Edit-dialog leaf components used by the admin content components (transitive sub-dependencies, #684)
import { ApiKeyDialogComponent } from './components/api-key-dialog/apikey-dialog.component';
import { CustomAttributeSectionDialogComponent } from './components/custom-attribute-section-dialog/custom-attribute-section-dialog.component';
import { CustomAttributeDefinitionDialogComponent } from './components/custom-attribute-definition-dialog/custom-attribute-definition-dialog.component';
import { SubscriptionDialogComponent } from './components/subscription-dialog/subscription-dialog.component';
import { SubscriptionSecretDialogComponent } from './components/subscription-secret-dialog/subscription-dialog.component';
// Injectable services backing the migrated event/subscription components (not providedIn:'root', #684)
import { EventService } from './services/event/eventService';
import { EventSubscriptionService } from './services/eventSubscription/eventSubscriptionService';

// Billing detail views (migrated 1:1 from @nexato/nx-core-module, #685)
import { BillingInvoiceListComponent } from './components/billing-invoice-list/billing-invoice-list.component';
import { BillingInvoiceReceiverViewComponent } from './components/billing-invoice-receiver-view/billing-invoice-receiver-view.component';
import { BillingLicenceViewComponent } from './components/billing-licence-view/billing-licence-view.component';
import { BillingPaymentMethodViewComponent } from './components/billing-payment-method-view/billing-payment-method-view.component';
// Edit-dialog leaf components used by the invoice-receiver-view (transitive sub-dependencies, #685)
import { BillingInvoiceReceiverEditAddressDialogComponent } from './components/billing-invoice-receiver-edit-address-dialog/billing-invoice-receiver-edit-address.component';
import { BillingInvoiceReceiverEditEmailDialogComponent } from './components/billing-invoice-receiver-edit-email-dialog/billing-invoice-receiver-edit-email.component';

// File-/Time sub-components + sidebar wrapper (migrated 1:1 from @nexato/nx-core-module, #686)
import { FileGalleryComponent } from './components/file-gallery/file-gallery.component';
import { FileListItemV2Component } from './components/file-list-item-v2/file-list-item-v2.component';
import { TimeEntryCategoryAccordionComponent } from './components/timeEntry-category-accordion/timeEntry-category-accordion.component';
import { SidebarWrapperComponent } from './components/sidebar-wrapper/sidebar-wrapper.component';
// <p-dialog> (image/pdf preview) + <ng2-pdfjs-viewer> used by file-list-item-v2 (#686)
import { DialogModule } from 'primeng/dialog';
import { PdfJsViewerModule } from 'ng2-pdfjs-viewer';

// Final NO_ERRORS_SCHEMA gaps (migrated 1:1 from @nexato/nx-core-module, #687)
import { EditTipTapInputComponent } from './components/edit-tipTap-form-control/edit-tipTap-form-control.component';
import { ActivityOwnerBadgeComponent } from './components/activity/activity-owner-badge/activity-owner-badge.component';
import { TimeEntryListItemComponent } from './components/timeEntry-list-item/timeEntry-list-item.component';

// Issue-status display components (migrated 1:1 from @nexato/nx-core-module, #688)
import { IssueStatusIconComponent } from './components/issue/issue-status-icon.component';
import { IssueStatusComponent } from './components/issue/issue-status.component';
// Entity-link-widget input components (migrated 1:1 from @nexato/nx-core-module, #688)
import { SingleEntityLinkWidgetInputComponent } from './components/single-entity-link-widget/single-entity-link-widget-input.component';
import { MultiEntityLinkWidgetInputComponent } from './components/multi-entity-link-widget/multi-entity-link-widget-input.component';
import { MultiEntityLinkWidgetComponent } from './components/multi-entity-link-widget/multi-entity-link-widget.component';
// Number-input components (migrated 1:1 from @nexato/nx-core-module, #688)
import { IncrementDecrementInputComponent } from './components/increment-decrement-input/increment-decrement-input.component';
import { EditNumberInputComponent } from './components/edit-number-form-control/edit-number-form-control.component';
import { InputTextModule } from 'primeng/inputtext';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextareaModule } from 'primeng/inputtextarea';
// newTabNavigation directive (migrated 1:1 from @nexato/nx-core-module, #688)
import { NewTabNavigationDirective } from './directives/new-tab-directive';
// Sort/Filter/Calendar-select components (migrated 1:1 from @nexato/nx-core-module, #688)
import { NxSortSelectComponent } from './components/sort-select/nx-sort-select';
import { NxFilterDateSelectComponent } from './components/filter-date-select/nx-filter-date-select';
import { EditCalendarInputComponent } from './components/edit-calendar-form-control/edit-calendar-form-control.component';
import { CalendarModule } from 'primeng/calendar';
// <p-colorPicker> in edit-color-form-control (#712)
import { ColorPickerModule } from 'primeng/colorpicker';
// Rest components (migrated 1:1 from @nexato/nx-core-module, #688)
import { EditCheckboxInputComponent } from './components/edit-checkbox-form-control/edit-checkbox-form-control.component';
import { UploaderStateViewComponent } from './components/uploader-state-view/uploader-state-view.component';

// Local 1:1 replacement for the library module @nexato/nx-core-module (#665).
// All declared components/directives/pipes are re-exported (via CORE_DECLARATIONS in
// exports) together with TranslateModule, so the rent-/order-/tour-planning domain
// modules keep receiving them once CoreModuleModule is retired.
const CORE_DECLARATIONS = [
  // activity renderer + 13 activity-type components migrated from nx-core-module (#688)
    ACTIVITY_COMPONENT_LIST,
    ActivityRendererComponent,
    ActivityStreamComponent,
    IssueListComponent,
    IssueViewComponent,
    IssueViewV2SidebarWrapperComponent,
    IssuesSettingsPageComponent,
    LabelListComponent,
    LabelViewComponent,
    TaskSettingsPageComponent,
    FormsListComponent,
    FormVariantComponent,
    TemplatesComponent,
    TemplateVariantComponent,
    EditSelectV2InputComponent,
    EditDateTimePeriodInputV2Component,
    NxTemplateDirective,
    // Shared UI primitives (migrated 1:1 from @nexato/nx-core-module, #671)
    DefaultButtonComponent,
    DefaultBoxComponent,
    HeadlineBoxComponent,
    // Admin page components
    BillingComponent,
    CompanyViewComponent,
    DeveloperPageComponent,
    GroupListComponent,
    GroupViewComponent,
    MobileDeviceListComponent,
    MobileDeviceViewComponent,
    NoAccessPageComponent,
    TenantListComponent,
    TenantViewComponent,
    TimeEntryCategoryListComponent,
    TimeEntryCategoryViewComponent,
    UserProfileComponent,
    UserViewComponent,
    UserListComponent,
    // Helper dialogs
    CompanyDialogComponent,
    GroupDialogComponent,
    AddRolesDialogComponent,
    AppVersionsDialogComponent,
    TenantDialogComponent,
    TimeEntryCategoryDialogDialogComponent,
    UserPreferredLanguageDialogComponent,
    UserDialogComponent,
    UserLicenceDialogComponent,
    UserPasswordDialogComponent,
    JoinPipe,
    ArrayTranslatePipe,
    // Generic dialog components (migrated 1:1 from @nexato/nx-core-module, #666)
    NxConfirmDialogComponent,
    EditDialogComponent,
    EditDialogDynamicContentDirective,
    ViewDialogComponent,
    ViewDialogDynamicContentDirective,
    // Domain dialog components (migrated 1:1 from @nexato/nx-core-module, #669)
    IssueCreateDialogComponent,
    TaskCreateDialogComponent,
    LabelDialogComponent,
    // Icon & mini-view display components (migrated 1:1 from @nexato/nx-core-module, #672)
    RelativeTimeComponent,
    UserNameComponent,
    LabelComponent,
    CommentsIconComponent,
    FilesIconComponent,
    NoteIconComponent,
    RelativeTimePipe,
    FutureRelativeTimePipe,
    // Job-Verwaltung (#746)
    TriggerStateBadgeComponent,
    TriggerTableComponent,
    JobListComponent,
    JobDetailComponent,
    // Box & Button UI primitives (migrated 1:1 from @nexato/nx-core-module, #673)
    EmptyBoxComponent,
    PageTitleBoxComponent,
    CommandButtonComponent,
    NxButtonComponent,
    // Edit form controls — Text/Select (migrated 1:1 from @nexato/nx-core-module, #674)
    EditTextInputComponent,
    EditTextfieldInputComponent,
    EditSelectInputComponent,
    // Country picker used by tenant-, company- and billing-invoice-receiver-address-dialog.
    // Missed when @nexato/nx-core-module was merged in — declared there, never exported (#751).
    EditSelectCountryInputComponent,
    EditMultiSelectInputComponent,
    AutoResizeTextareaDirective,
    // Edit form control — Color (never migrated from @nexato/nx-core-module during #669, #712)
    EditColorInputComponent,
    // Edit form control — Chips (never migrated from @nexato/nx-core-module during #669, referenced by location-dialog)
    EditChipsInputComponent,
    // Notification settings section + row in the user profile (#735)
    NotificationSettingsListComponent,
    NotificationTypeRowComponent,
    // Notification diagnostics for support: parent (Admin) and child (Delivery) level (#737)
    NotificationDiagnosticsPageComponent,
    NotificationDeliveryListComponent,
    NotificationDeliveryRowComponent,
    NotificationAdminDetailComponent,
    NotificationAdminRowComponent,
    NotificationAdminListComponent,
    // Announcement management for Nexato platform broadcasts (#738)
    AnnouncementListComponent,
    AnnouncementRowComponent,
    AnnouncementFormComponent,
    AnnouncementBodyInputComponent,
    // Filter controls (migrated 1:1 from @nexato/nx-core-module, #676)
    NxFilterMultiSelectItemComponent,
    NxFilterMultiSelectComponent,
    NxFilterNumberSelectComponent,
    // Time-/date-entry controls + deps (migrated 1:1 from @nexato/nx-core-module, #675)
    EditTimeEntriesInputComponent,
    TimeEntryDialogDialogComponent,
    SecondsToDurationPipe,
    // File- & TimeEntry-views (migrated 1:1 from @nexato/nx-core-module, #677)
    FileListComponent,
    FileGalleryDisplayComponent,
    TimeEntryListComponent,
    TimeEntriesSumComponent,
    TimeEntryCategoriesSumComponent,
    // Generic UI widgets (migrated 1:1 from @nexato/nx-core-module, #680)
    CountBadgeComponent,
    NxFilterDropdownItemComponent,
    NxFilterDropdownComponent,
    PageTitleBoxToplineComponent,
    SimplePaginatorComponent,
    TableHeaderComponent,
    // Entity-link display components (migrated 1:1 from @nexato/nx-core-module, #681)
    EntityLinkListComponent,
    SingleEntityLinkWidgetComponent,
    // Issue display/type components (migrated 1:1 from @nexato/nx-core-module, #682)
    IssueRowComponent,
    IssueTypeIconComponent,
    IssueTypesComponent,
    IssueStatusGrojupsComponent,
    // Issue command widgets + label/activity widget (migrated 1:1 from @nexato/nx-core-module, #683)
    AssignIssueWidgetComponent,
    ChangeIssueStatusWidgetComponent,
    IssueDueDateTimeWidgetComponent,
    SelectFormWidgetComponent,
    UpdateIssueLocationWidgetComponent,
    IssueLocationWidgetInputComponent,
    LabelWidgetComponent,
    LabelWidgetInputComponent,
    ActivityCommentCreatedCreateCommandComponent,
    // nx-edit-multiselect-v2-input — transitive sub-dependency of label-widget (#683)
    EditMultiSelectV2InputComponent,
    // Admin-/Developer-page content components (migrated 1:1 from @nexato/nx-core-module, #684)
    ApiKeysComponent,
    CustomAttributesComponent,
    CustomAttributeSectionComponent,
    CustomAttributeDefinitionComponent,
    EventsComponent,
    HttpLogsComponent,
    SubscriptionsComponent,
    TaskTypesComponent,
    // Edit-dialog leaf components used by the admin content components (transitive sub-dependencies, #684)
    ApiKeyDialogComponent,
    CustomAttributeSectionDialogComponent,
    CustomAttributeDefinitionDialogComponent,
    SubscriptionDialogComponent,
    SubscriptionSecretDialogComponent,
    // Billing detail views (migrated 1:1 from @nexato/nx-core-module, #685)
    BillingInvoiceListComponent,
    BillingInvoiceReceiverViewComponent,
    BillingLicenceViewComponent,
    BillingPaymentMethodViewComponent,
    // Edit-dialog leaf components used by the invoice-receiver-view (transitive sub-dependencies, #685)
    BillingInvoiceReceiverEditAddressDialogComponent,
    BillingInvoiceReceiverEditEmailDialogComponent,
    // File-/Time sub-components + sidebar wrapper (migrated 1:1 from @nexato/nx-core-module, #686)
    FileGalleryComponent,
    FileListItemV2Component,
    TimeEntryCategoryAccordionComponent,
    SidebarWrapperComponent,
    // Final NO_ERRORS_SCHEMA gaps (migrated 1:1 from @nexato/nx-core-module, #687):
    // nx-edit-tipTap-input (3 templates), app-activity-owner-badge (file-list-item-v2
    // + timeEntry-list-item), app-timeEntry-list-item (timeEntry-category-accordion)
    EditTipTapInputComponent,
    ActivityOwnerBadgeComponent,
    TimeEntryListItemComponent,
    // Issue-status display components (migrated 1:1 from @nexato/nx-core-module, #688)
    IssueStatusIconComponent,
    IssueStatusComponent,
    // nx-default-box-v2 component (migrated 1:1 from @nexato/nx-core-module, #688)
    DefaultBoxV2Component,
    // Entity-link-widget input components (migrated 1:1 from @nexato/nx-core-module, #688)
    SingleEntityLinkWidgetInputComponent,
    MultiEntityLinkWidgetInputComponent,
    MultiEntityLinkWidgetComponent,
    // Number-input components (migrated 1:1 from @nexato/nx-core-module, #688)
    IncrementDecrementInputComponent,
    EditNumberInputComponent,
    // newTabNavigation directive (migrated 1:1 from @nexato/nx-core-module, #688)
    NewTabNavigationDirective,
    // Sort/Filter/Calendar-select components (migrated 1:1 from @nexato/nx-core-module, #688)
    NxSortSelectComponent,
    NxFilterDateSelectComponent,
    EditCalendarInputComponent,
    // Rest components (migrated 1:1 from @nexato/nx-core-module, #688)
    EditCheckboxInputComponent,
    UploaderStateViewComponent,
];

@NgModule({
  declarations: CORE_DECLARATIONS,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    TranslateLabelsPipe,
    // PrimeNG modules required by the migrated UI primitives (#671)
    SharedModule,
    ButtonModule,
    MenuModule,
    TooltipModule,
    // Required by the migrated filter controls (#676): p-overlay / p-scroller
    OverlayModule,
    ScrollerModule,
    // Standalone time-entry control (migrated 1:1 from @nexato/nx-core-module, #675)
    AnchorDateTimeInputComponent,
    // Required by the migrated time-entry-dialog (#675): typed p-dropdown ref
    DropdownModule,
    // Modules backing the migrated list/admin/file components (#679) — previously
    // masked by NO_ERRORS_SCHEMA. Required for p-table (incl. p-tableCheckbox,
    // p-tableHeaderCheckbox, p-columnFilter), p-tabView/p-tabPanel, p-tag,
    // p-overlayPanel.
    TableModule,
    TabViewModule,
    TagModule,
    OverlayPanelModule,
    // <lightgallery> in file-gallery-display (#677)
    LightgalleryModule,
    // <ace> code editor in form-variant / template-variant (developer/template views)
    AceModule,
    // <p-progressSpinner> in single-entity-link-widget (#681)
    ProgressSpinnerModule,
    // <p-multiSelect> in edit-multiSelect-v2-form-control, backing the label widget (#683)
    MultiSelectModule,
    // <p-dialog> + <ng2-pdfjs-viewer> in file-list-item-v2 (#686)
    DialogModule,
    PdfJsViewerModule,
    // PrimeNG icon components (SearchIcon/CheckIcon/TimesIcon/TimesCircleIcon) and
    // CheckboxModule (p-checkbox [binary]) — previously masked by NO_ERRORS_SCHEMA (#688)
    SearchIcon,
    CheckIcon,
    TimesIcon,
    TimesCircleIcon,
    CheckboxModule,
    // <input pInputText> in edit-number-form-control (#688)
    InputTextModule,
    // <p-inputGroup>, <p-inputGroupAddon> for increment-decrement-input — Phase 2.1 (#696)
    InputGroupModule,
    InputGroupAddonModule,
    InputTextareaModule,
    // <p-calendar> in filter-date-select + edit-calendar-form-control (#688)
    CalendarModule,
    // <p-colorPicker> in edit-color-form-control (#712)
    ColorPickerModule,
    // <p-chips> in edit-chips-form-control (externalIds field on location-dialog)
    ChipsModule,
  ],
  exports: [
    CORE_DECLARATIONS,
    TranslateModule,
    // Standalone imports re-exported for the domain modules (previously provided
    // to them by the library CoreModuleModule, #665):
    // translateLabels pipe (order-/rent-module templates) and nx-anchor-datetime-input
    // (tour-planning tour-settings-dialog).
    TranslateLabelsPipe,
    AnchorDateTimeInputComponent,
  ],
  providers: [
    TenantsService,
    ApiKeysService,
    ConnectedDeviceSettingsService,
    GroupsService,
    // Injectable service backing the migrated time-entry controls (#675)
    TimeEntryService,
    // Injectable service backing the migrated FileListComponent (#677)
    FilesService,
    // Injectable service backing the migrated single-entity-link-widget (#681)
    LinkableService,
    // Injectable services backing the migrated issue display components (#682):
    // IssueRowRenderer (Issue entity-row registry) + IssueStatusGroupService (app-issue-status-groups)
    IssueRowRenderer,
    IssueStatusGroupService,
    // Injectable services backing the migrated issue command + label widgets (#683):
    // WidgetCoordinatorService (per-widget cancel coordination), LocationService
    // (update-location widget + input), RolesService (activity create-command),
    // LabelService (label-widget-input). All declared @Injectable() without
    // providedIn:'root', so they must be provided here.
    WidgetCoordinatorService,
    LocationService,
    RolesService,
    LabelService,
    // Injectable services backing the migrated event/subscription admin components (#684):
    // EventService (resend/close), EventSubscriptionService (subscriptions CRUD + secret).
    // Both declared @Injectable() without providedIn:'root', so they must be provided here.
    EventService,
    EventSubscriptionService,
    // injected by EditCalendarInputComponent (#688)
    DatePipe,
    // injected by UploaderStateViewComponent (#688)
    CurrencyPipe,
    // activity-type → component registry for ActivityRendererComponent (#688)
    { provide: ACTIVITY_COMPONENTS, useFactory: createActivityComponentRegistry },
    // Provider-Parität nach CoreModuleModule-Ablösung (#665): diese Services sind
    // @Injectable() ohne providedIn:'root' und wurden zuvor vom Library-Modul bereitgestellt (#690).
    IssueTypeService,
    CustomAttributesService,
    FormVariantsService,
    TemplateVariantsService,
    IssueStatusService,
  ],
})
export class CoreComponentsModule {}
